import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import useBodyScrollLock from "../hooks/useBodyScrollLock";
import {
  approveBudgetSubmission,
  createBudgetSubmission,
  getBudgetSubmissions,
  submitBudgetForApproval,
} from "../api/budgetApi";
import { BUDGET_ORGANIZATIONS } from "../constants/organizations";
import { BUDGET_PLAN_SCOPE, BUDGET_PLAN_SCOPE_LABELS } from "../constants/budgetPlanScope";
import { isHodTier, isSuperAdminTier } from "../constants/orgTiers";
import { formatInr } from "../utils/formatInr";
import BudgetApprovalDetailModal from "./BudgetApprovalDetailModal";

const PANEL_STYLES = {
  hod: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    accent: "#2C63AE",
  },
  pending: {
    background: "linear-gradient(135deg, #fffbeb 0%, #fef9c3 50%, #fff7ed 100%)",
    border: "2px solid #f59e0b",
    accent: "#b45309",
    boxShadow: "0 4px 14px rgba(245, 158, 11, 0.15)",
  },
  approved: {
    background: "linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)",
    border: "2px solid #22c55e",
    accent: "#15803d",
    boxShadow: "0 2px 8px rgba(34, 197, 94, 0.12)",
  },
};

const STATUS_STYLES = {
  draft: { bg: "#f1f5f9", color: "#475569", label: "Draft" },
  pending: { bg: "#fef3c7", color: "#b45309", label: "Pending" },
  approved: { bg: "#dcfce7", color: "#15803d", label: "Approved" },
  rejected: { bg: "#fee2e2", color: "#b91c1c", label: "Rejected" },
};

const btn = (bg, color = "#fff") => ({
  border: "none",
  borderRadius: 8,
  padding: "6px 12px",
  background: bg,
  color,
  fontWeight: 700,
  fontSize: "0.78rem",
  cursor: "pointer",
  whiteSpace: "nowrap",
});

function StatusChip({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.draft;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 8px",
        borderRadius: 999,
        fontSize: "0.68rem",
        fontWeight: 800,
        background: s.bg,
        color: s.color,
        textTransform: "uppercase",
      }}
    >
      {s.label}
    </span>
  );
}

export default function BudgetSubmissionsPanel({
  role,
  department,
  budgetYear,
  organizationView,
  departmentProjects = [],
  selectedSubmissionId,
  onSelectSubmission,
  onBudgetChange,
  portfolioTotalAllocated,
}) {
  const qc = useQueryClient();
  const isSuper = isSuperAdminTier(role);
  const isHod = isHodTier(role);
  const [detailId, setDetailId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  useBodyScrollLock(createOpen);
  const [newTitle, setNewTitle] = useState("");
  const [newOrg, setNewOrg] = useState(organizationView || "");
  const [newScopeType, setNewScopeType] = useState(BUDGET_PLAN_SCOPE.DEPARTMENT);
  const [newProjectId, setNewProjectId] = useState("");
  const [newPlannedAmount, setNewPlannedAmount] = useState("");

  const scopeLabel = (row) => {
    if (row.scopeType === BUDGET_PLAN_SCOPE.PROJECT) {
      return row.projectName || row.projectId || "Project";
    }
    return "Team allocation";
  };

  const listParams = useMemo(
    () => ({
      ...(department ? { department } : {}),
      year: budgetYear,
      ...(isSuper ? {} : {}),
    }),
    [department, budgetYear, isSuper]
  );

  const submissionsQuery = useQuery({
    queryKey: ["budget-submissions", listParams, organizationView],
    queryFn: () => getBudgetSubmissions(listParams).then((r) => r.data),
    enabled: isHod || isSuper,
    staleTime: 0,
  });

  const allSubmissions = submissionsQuery.data || [];
  const submissions = useMemo(() => {
    if (!organizationView) return allSubmissions;
    return allSubmissions.filter(
      (s) => !s.organization || String(s.organization).toUpperCase() === organizationView
    );
  }, [allSubmissions, organizationView]);

  const pendingSubmissions = useMemo(
    () => submissions.filter((s) => s.status === "pending"),
    [submissions]
  );

  const approvedSubmissions = useMemo(
    () => submissions.filter((s) => s.status === "approved"),
    [submissions]
  );

  const openPlanModal = (row) => {
    onSelectSubmission?.(row.id);
    setDetailId(row.id);
  };

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["budget-submissions"] });
    qc.invalidateQueries({ queryKey: ["budget-submission-current"] });
    qc.invalidateQueries({ queryKey: ["budget-portfolio"] });
  };

  const createMut = useMutation({
    mutationFn: (body) => createBudgetSubmission(body).then((r) => r.data),
    onSuccess: (created) => {
      toast.success("Budget plan created — edit project budgets below, then submit");
      setCreateOpen(false);
      setNewTitle("");
      onSelectSubmission?.(created.id);
      invalidate();
    },
    onError: (e) => toast.error(e.response?.data?.message || "Create failed"),
  });

  const submitMut = useMutation({
    mutationFn: (id) => submitBudgetForApproval(id).then((r) => r.data),
    onSuccess: () => {
      toast.success("Submitted for approval — superadmin will be notified");
      invalidate();
    },
    onError: (e) => toast.error(e.response?.data?.message || "Submit failed"),
  });

  const approveMut = useMutation({
    mutationFn: (id) => approveBudgetSubmission(id).then((r) => r.data),
    onSuccess: (data) => {
      const applied = data?.appliedTotalAllocated;
      toast.success(
        applied != null
          ? `Budget approved — ${formatInr(applied)} allocated across projects`
          : "Budget approved — allocations updated"
      );
      invalidate();
    },
    onError: (e) => toast.error(e.response?.data?.message || "Approve failed"),
  });

  if (!isHod && !isSuper) return null;

  const showHodPlans = isHod && !isSuper && department;
  const showSuperQueue = isSuper && pendingSubmissions.length > 0;

  if (!showHodPlans && !showSuperQueue && approvedSubmissions.length === 0) return null;

  return (
    <div style={{ marginBottom: 16 }}>
      {showHodPlans ? (
        <div
          style={{
            background: PANEL_STYLES.hod.background,
            border: PANEL_STYLES.hod.border,
            borderRadius: 14,
            overflow: "hidden",
            marginBottom: showSuperQueue || approvedSubmissions.length > 0 ? 12 : 0,
          }}
        >
          <div
            style={{
              padding: "12px 16px",
              borderBottom: "1px solid #e2e8f0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <div>
              <div style={{ fontWeight: 800, color: "#0f172a" }}>My budget plans</div>
              <div style={{ fontSize: "0.78rem", color: "#64748b" }}>
                {department} · {budgetYear} — create plans, edit projects below, then submit
              </div>
            </div>
            <button type="button" style={btn("#2C63AE")} onClick={() => {
              setNewScopeType(BUDGET_PLAN_SCOPE.DEPARTMENT);
              setNewProjectId("");
              setCreateOpen(true);
            }}>
              + New budget plan
            </button>
          </div>

          {submissions.length === 0 ? (
            <div style={{ padding: "20px 16px", color: "#64748b", fontSize: "0.85rem" }}>
              No budget plans yet. Click <strong>New budget plan</strong>, then set allocations on projects in the table below.
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
              <thead>
                <tr style={{ background: "#f8fafc", textAlign: "left" }}>
                  {["Plan", "Scope", "Requested budget", "Organisation", "Spend (actual)", "Status", ""].map((h) => (
                    <th key={h || "a"} style={{ padding: "10px 14px", fontSize: "0.7rem", color: "#64748b", fontWeight: 800 }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {submissions.map((row) => {
                  const active = row.id === selectedSubmissionId;
                  const canEditRow = row.status === "draft" || row.status === "rejected";
                  const rowHighlight =
                    row.status === "pending"
                      ? { background: "#fffbeb" }
                      : row.status === "approved"
                        ? { background: "#f0fdf4" }
                        : active
                          ? { background: "#EAF6FD" }
                          : undefined;
                  return (
                    <tr
                      key={row.id}
                      style={{
                        borderTop: "1px solid #f1f5f9",
                        cursor: "pointer",
                        ...rowHighlight,
                      }}
                      onClick={() => onSelectSubmission?.(row.id)}
                    >
                      <td style={{ padding: "12px 14px", fontWeight: 700 }}>{row.title}</td>
                      <td style={{ padding: "12px 14px", color: "#475569" }}>{scopeLabel(row)}</td>
                      <td style={{ padding: "12px 14px", fontWeight: 700, color: "#0f766e" }}>
                        {row.plannedAmountInr != null ? formatInr(row.plannedAmountInr) : "—"}
                      </td>
                      <td style={{ padding: "12px 14px" }}>{row.organization || "All orgs"}</td>
                      <td style={{ padding: "12px 14px" }}>{formatInr(row.totalSpend)}</td>
                      <td style={{ padding: "12px 14px" }}>
                        <StatusChip status={row.status} />
                      </td>
                      <td style={{ padding: "12px 14px", textAlign: "right" }}>
                        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", flexWrap: "wrap" }}>
                          <button
                            type="button"
                            style={btn(canEditRow ? "#2C63AE" : "#e2e8f0", canEditRow ? "#fff" : "#334155")}
                            onClick={(e) => {
                              e.stopPropagation();
                              openPlanModal(row);
                            }}
                          >
                            {canEditRow ? "Edit" : "View"}
                          </button>
                          {canEditRow ? (
                            <button
                              type="button"
                              style={btn("#0f766e")}
                              disabled={submitMut.isPending}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (row.plannedAmountInr == null || Number(row.plannedAmountInr) < 0) {
                                  toast.error("Set requested budget before submitting — use Edit");
                                  return;
                                }
                                submitMut.mutate(row.id);
                              }}
                            >
                              Submit
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {submissions.some((s) => s.id === selectedSubmissionId && (s.status === "draft" || s.status === "rejected")) ? (
            <div style={{ padding: "10px 16px", borderTop: "1px solid #e2e8f0", fontSize: "0.8rem", color: "#1272B3", background: "#f8fafc" }}>
              <strong>Editing:</strong> use <strong>Edit</strong> for plan details, <strong>Manage</strong> on projects below for budget lines, then <strong>Submit</strong> to send for approval.
            </div>
          ) : null}
        </div>
      ) : null}

      {showSuperQueue ? (
        <div
          style={{
            background: PANEL_STYLES.pending.background,
            border: PANEL_STYLES.pending.border,
            borderRadius: 14,
            overflow: "hidden",
            boxShadow: PANEL_STYLES.pending.boxShadow,
            marginBottom: 12,
          }}
        >
          <div
            style={{
              padding: "12px 16px",
              borderBottom: "1px solid #fcd34d",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  background: "#f59e0b",
                  color: "#fff",
                  fontWeight: 900,
                  fontSize: "0.85rem",
                }}
              >
                !
              </span>
              <div>
                <div style={{ fontWeight: 800, color: PANEL_STYLES.pending.accent }}>
                  Pending approvals ({pendingSubmissions.length})
                </div>
                <div style={{ fontSize: "0.76rem", color: "#92400e" }}>
                  Review and approve — allocations update on the portfolio below after approval
                </div>
              </div>
            </div>
            <span
              style={{
                padding: "4px 10px",
                borderRadius: 999,
                background: "#f59e0b",
                color: "#fff",
                fontSize: "0.7rem",
                fontWeight: 800,
                textTransform: "uppercase",
              }}
            >
              Action required
            </span>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
            <thead>
              <tr style={{ background: "#f8fafc", textAlign: "left" }}>
                {["Department", "Year", "Scope", "Plan", "Requested", "Spend", "By", ""].map((h) => (
                  <th key={h || "a"} style={{ padding: "10px 14px", fontSize: "0.7rem", color: "#64748b", fontWeight: 800 }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pendingSubmissions.map((row) => (
                <tr key={row.id} style={{ borderTop: "1px solid #fde68a", background: "rgba(255,255,255,0.6)" }}>
                  <td style={{ padding: "12px 14px", fontWeight: 600 }}>{row.department}</td>
                  <td style={{ padding: "12px 14px" }}>{row.budgetYear}</td>
                  <td style={{ padding: "12px 14px" }}>{scopeLabel(row)}</td>
                  <td style={{ padding: "12px 14px" }}>{row.title}</td>
                  <td style={{ padding: "12px 14px", fontWeight: 700, color: "#0f766e" }}>
                    {row.plannedAmountInr != null ? formatInr(row.plannedAmountInr) : "—"}
                  </td>
                  <td style={{ padding: "12px 14px" }}>{formatInr(row.totalSpend)}</td>
                  <td style={{ padding: "12px 14px" }}>{row.submittedByName || "—"}</td>
                  <td style={{ padding: "12px 14px", textAlign: "right" }}>
                    <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", flexWrap: "wrap" }}>
                      <button type="button" style={btn("#d97706")} onClick={() => setDetailId(row.id)}>
                        Review
                      </button>
                      <button
                        type="button"
                        style={btn("#15803d")}
                        disabled={approveMut.isPending}
                        onClick={() => approveMut.mutate(row.id)}
                      >
                        Approve
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {!showSuperQueue && approvedSubmissions.length > 0 ? (
        <div
          style={{
            background: PANEL_STYLES.approved.background,
            border: PANEL_STYLES.approved.border,
            borderRadius: 12,
            padding: "12px 16px",
            marginBottom: 12,
            boxShadow: PANEL_STYLES.approved.boxShadow,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div>
            <div style={{ fontWeight: 800, color: PANEL_STYLES.approved.accent, fontSize: "0.9rem" }}>
              Approved budget active
            </div>
            <div style={{ fontSize: "0.78rem", color: "#166534", marginTop: 2 }}>
              {approvedSubmissions.length} approved plan{approvedSubmissions.length > 1 ? "s" : ""} — portfolio allocations below reflect approved amounts
            </div>
          </div>
          <div style={{ fontSize: "0.82rem", fontWeight: 700, color: "#15803d" }}>
            {formatInr(portfolioTotalAllocated ?? approvedSubmissions.reduce((s, p) => s + (p.totalAllocated || p.plannedAmountInr || 0), 0))} allocated
          </div>
        </div>
      ) : null}

      {createOpen ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,23,42,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1400,
            padding: 16,
          }}
          onClick={() => setCreateOpen(false)}
        >
          <div
            style={{ background: "#fff", borderRadius: 14, padding: 20, width: "100%", maxWidth: 420 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontWeight: 800, marginBottom: 12 }}>New budget plan</div>

            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: "0.8rem", fontWeight: 600, marginBottom: 6 }}>Plan type *</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.85rem", cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="scopeType"
                    checked={newScopeType === BUDGET_PLAN_SCOPE.DEPARTMENT}
                    onChange={() => {
                      setNewScopeType(BUDGET_PLAN_SCOPE.DEPARTMENT);
                      setNewProjectId("");
                    }}
                  />
                  {BUDGET_PLAN_SCOPE_LABELS.department} — entire {department || "department"} team
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.85rem", cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="scopeType"
                    checked={newScopeType === BUDGET_PLAN_SCOPE.PROJECT}
                    onChange={() => setNewScopeType(BUDGET_PLAN_SCOPE.PROJECT)}
                  />
                  {BUDGET_PLAN_SCOPE_LABELS.project} — one project only
                </label>
              </div>
            </div>

            {newScopeType === BUDGET_PLAN_SCOPE.PROJECT ? (
              <>
                <label style={{ display: "block", fontSize: "0.8rem", fontWeight: 600, marginBottom: 4 }}>Project *</label>
                <select
                  value={newProjectId}
                  onChange={(e) => setNewProjectId(e.target.value)}
                  style={{ width: "100%", marginBottom: 12, padding: "8px 10px", borderRadius: 8, border: "1px solid #cbd5e1" }}
                >
                  <option value="">Select project…</option>
                  {departmentProjects.map((p) => (
                    <option key={p.projectId} value={p.projectId}>{p.projectName}</option>
                  ))}
                </select>
              </>
            ) : null}

            <label style={{ display: "block", fontSize: "0.8rem", fontWeight: 600, marginBottom: 4 }}>Plan name *</label>
            <input
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="e.g. Q2 Tech infrastructure"
              style={{ width: "100%", marginBottom: 12, padding: "8px 10px", borderRadius: 8, border: "1px solid #cbd5e1" }}
            />
            <label style={{ display: "block", fontSize: "0.8rem", fontWeight: 600, marginBottom: 4 }}>Requested budget (INR) *</label>
            <input
              type="number"
              min="0"
              step="1"
              value={newPlannedAmount}
              onChange={(e) => setNewPlannedAmount(e.target.value)}
              placeholder="e.g. 10000000"
              style={{ width: "100%", marginBottom: 12, padding: "8px 10px", borderRadius: 8, border: "1px solid #cbd5e1" }}
            />

            <label style={{ display: "block", fontSize: "0.8rem", fontWeight: 600, marginBottom: 4 }}>Organisation (optional)</label>
            <select
              value={newOrg}
              onChange={(e) => setNewOrg(e.target.value)}
              style={{ width: "100%", marginBottom: 16, padding: "8px 10px", borderRadius: 8, border: "1px solid #cbd5e1" }}
            >
              <option value="">All organisations</option>
              {BUDGET_ORGANIZATIONS.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button type="button" style={btn("#e2e8f0", "#334155")} onClick={() => setCreateOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                style={btn("#2C63AE")}
                disabled={
                  !newTitle.trim() ||
                  !newPlannedAmount ||
                  Number(newPlannedAmount) < 0 ||
                  createMut.isPending ||
                  (newScopeType === BUDGET_PLAN_SCOPE.PROJECT && !newProjectId)
                }
                onClick={() =>
                  createMut.mutate({
                    title: newTitle.trim(),
                    department,
                    budgetYear,
                    organization: newOrg || undefined,
                    scopeType: newScopeType,
                    plannedAmountInr: Number(newPlannedAmount),
                    ...(newScopeType === BUDGET_PLAN_SCOPE.PROJECT ? { projectId: newProjectId } : {}),
                  })
                }
              >
                Create plan
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {detailId ? (
        <BudgetApprovalDetailModal
          submissionId={detailId}
          isSuper={isSuper}
          onClose={() => setDetailId(null)}
          onSelectPlan={onSelectSubmission}
          onBudgetChange={onBudgetChange}
        />
      ) : null}
    </div>
  );
}
